import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  effect,
  Inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '@examdojo/core/button';
import { NumberInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import type { IconPickerSelection } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';
import {
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToggle,
  IonToolbar,
  ToggleCustomEvent,
} from '@ionic/angular/standalone';
import * as go from 'gojs';
import { type ThemeColorName } from '@examdojo/styles';
import { GojsDiagramModel, GojsDiagramNode } from '../../../model';
import { resolveGojsColorPalette } from '../../../gojs/gojs-color.util';
import { parseAccordionIcon, serializeAccordionIcon } from '../accordion/accordion-icon.util';
import { parseSimpleLatex } from '../../../gojs/gojs-diagram.util';
import { resolveGojsIcon } from '../../../gojs/gojs-icon.util';
import { GojsDiagramEditorService } from './gojs-diagram-editor.service';
import { GojsNodePropertiesPanelComponent } from './gojs-node-properties-panel.component';
import { GojsLinkPropertiesPanelComponent } from './gojs-link-properties-panel.component';

/** Runtime node data shape used inside the GoJS diagram editor (extends the serialized model). */
interface GojsEditorNodeData extends GojsDiagramNode {
  bg_color?: string;
  text_color?: string;
  border_color?: string;
  icon_text?: string;
  icon_src?: string;
  is_math?: boolean;
  z_order?: number;
  node_width?: number;
  node_height?: number;
  has_border?: boolean;
}

interface SelectOption {
  label: string;
  value: string;
}

/** Delay (ms) to wait for the Ionic modal open animation before initializing the GoJS editor. */
const MODAL_INIT_DELAY_MS = 150;

@Component({
  selector: 'dojo-gojs-diagram-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonToggle,
    ButtonComponent,
    NumberInputComponent,
    SingleSelectComponent,
    GojsNodePropertiesPanelComponent,
    GojsLinkPropertiesPanelComponent,
  ],
  templateUrl: './gojs-diagram-editor.component.html',
  styleUrl: './gojs-diagram-editor.component.scss',
  providers: [GojsDiagramEditorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsDiagramEditorComponent implements AfterViewInit, OnDestroy {
  constructor(
    private readonly editorService: GojsDiagramEditorService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    effect(() => {
      if (this.isOpen() && !this.initialized) {
        this.initialized = true;
        setTimeout(() => this.initEditor(), MODAL_INIT_DELAY_MS);
      }
    });
  }

  readonly model = input.required<GojsDiagramModel>();
  readonly layoutType = input<string>('tree');
  readonly height = input<number>(300);
  readonly isOpen = input<boolean>(false);

  readonly modelChange = output<{ model: GojsDiagramModel; layoutType?: string; height?: number }>();
  readonly editorClose = output<void>();

  private pendingResult: { model: GojsDiagramModel; layoutType?: string; height?: number } | null = null;

  @ViewChild(IonModal) private readonly modal!: IonModal;
  @ViewChild('editorDiv', { static: false }) editorDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('paletteDiv', { static: false }) paletteDiv!: ElementRef<HTMLDivElement>;

  readonly selectedNodeData = this.editorService.selectedNodeData;
  readonly selectedLinkData = this.editorService.selectedLinkData;
  readonly autoLayout = signal(true);
  readonly showBackground = signal(true);
  readonly showIconPicker = signal(false);
  readonly parsedNodeIcon = computed(() => {
    const data = this.selectedNodeData();
    const icon = data?.['icon'] as string | undefined;
    return icon ? parseAccordionIcon(icon) : null;
  });

  // Form controls
  readonly layoutCtrl = new FormControl<string>('tree', { nonNullable: true });
  readonly heightCtrl = new FormControl<number>(300, { nonNullable: true });
  readonly nodeTextCtrl = new FormControl<string>('', { nonNullable: true });
  readonly nodeShapeCtrl = new FormControl<string>('RoundedRectangle', { nonNullable: true });
  readonly nodeColorThemeCtrl = new FormControl<string>('neutral', { nonNullable: true });
  readonly nodeFontSizeCtrl = new FormControl<number>(14, { nonNullable: true });
  readonly nodeFontWeightCtrl = new FormControl<string>('normal', { nonNullable: true });
  readonly nodeWidthCtrl = new FormControl<number | null>(null);
  readonly nodeHeightCtrl = new FormControl<number | null>(null);
  readonly linkWidthCtrl = new FormControl<number>(1.5, { nonNullable: true });
  readonly linkColorCtrl = new FormControl<string>('#8a8886', { nonNullable: true });
  readonly linkArrowCtrl = new FormControl<string>('Triangle', { nonNullable: true });

  // Select options
  readonly layoutOptions: SelectOption[] = [
    { label: 'Tree', value: 'tree' },
    { label: 'Force Directed', value: 'force' },
    { label: 'Layered', value: 'layered' },
    { label: 'Grid', value: 'grid' },
    { label: 'None (Manual)', value: 'none' },
  ];

  readonly shapeOptions: SelectOption[] = [
    { label: 'Rounded Rectangle', value: 'RoundedRectangle' },
    { label: 'Rectangle', value: 'Rectangle' },
    { label: 'Ellipse', value: 'Ellipse' },
    { label: 'Diamond', value: 'Diamond' },
  ];

  readonly colorThemeOptions: SelectOption[] = [
    { label: 'Neutral', value: 'neutral' },
    { label: 'Amber', value: 'amber' },
    { label: 'Teal', value: 'teal' },
    { label: 'Green', value: 'green' },
    { label: 'Sky', value: 'sky' },
    { label: 'Indigo', value: 'indigo' },
    { label: 'Pink', value: 'pink' },
    { label: 'Red', value: 'red' },
    { label: 'Orange', value: 'orange' },
    { label: 'Purple', value: 'purple' },
  ];

  readonly fontWeightOptions: SelectOption[] = [
    { label: 'Normal', value: 'normal' },
    { label: 'Bold', value: 'bold' },
  ];

  readonly arrowOptions: SelectOption[] = [
    { label: 'Filled Arrow', value: 'Triangle' },
    { label: 'Standard Arrow', value: 'Standard' },
    { label: 'Open Arrow', value: 'OpenTriangle' },
    { label: 'Circle', value: 'Circle' },
    { label: 'None', value: 'None' },
  ];

  readonly linkColorOptions: Array<{ label: string; value: string }> = [
    { label: 'Gray', value: '#8a8886' },
    { label: 'Dark', value: '#374151' },
    { label: 'Indigo', value: '#5b66f9' },
    { label: 'Sky', value: '#0d95ea' },
    { label: 'Teal', value: '#10bba2' },
    { label: 'Green', value: '#27c027' },
    { label: 'Amber', value: '#f9a107' },
    { label: 'Orange', value: '#f77409' },
    { label: 'Red', value: '#f73c42' },
    { label: 'Pink', value: '#e94b7a' },
    { label: 'Purple', value: '#9e5af2' },
  ];

  private initialized = false;

  ngAfterViewInit(): void {
    this.layoutCtrl.setValue(this.layoutType() ?? 'tree');
    this.heightCtrl.setValue(this.height() ?? 300);
    if (this.layoutType() === 'none') {
      this.autoLayout.set(false);
    }
  }

  ngOnDestroy(): void {
    this.editorService.destroy();
  }

  onColorThemeChange(theme: string): void {
    this.nodeColorThemeCtrl.setValue(theme, { emitEvent: false });
    const palette = resolveGojsColorPalette(this.document);
    const colors = palette[theme as ThemeColorName];
    if (!colors) {
      return;
    }

    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }

    const displayColors = this.showBackground() ? colors : palette['neutral'];
    const selected = diagram.selection.first();
    if (selected instanceof go.Node) {
      const data = selected.data as GojsEditorNodeData;
      diagram.model.startTransaction('update color theme');
      diagram.model.setDataProperty(data, 'color_theme', theme);
      diagram.model.setDataProperty(data, 'bg_color', displayColors.bg);
      diagram.model.setDataProperty(data, 'text_color', displayColors.text);
      diagram.model.setDataProperty(data, 'border_color', displayColors.border);
      diagram.model.commitTransaction('update color theme');
      this.selectedNodeData.set({ ...data });
    }
  }

  onBorderToggle(checked: boolean): void {
    this.updateNodeProp('has_border', checked);
  }

  onNodeIconSelected(selection: IconPickerSelection): void {
    const serialized = serializeAccordionIcon(selection);
    const diagram = this.editorService.getDiagram();
    if (!serialized || !diagram) {
      this.showIconPicker.set(false);
      return;
    }

    let iconData: ReturnType<typeof resolveGojsIcon>;
    try {
      iconData = resolveGojsIcon(serialized);
    } catch {
      this.showIconPicker.set(false);
      return;
    }
    const selected = diagram.selection.first();
    if (selected instanceof go.Node) {
      const data = selected.data as GojsEditorNodeData;
      diagram.model.startTransaction('update icon');
      diagram.model.setDataProperty(data, 'icon', serialized);
      diagram.model.setDataProperty(data, 'icon_text', iconData.icon_text);
      diagram.model.setDataProperty(data, 'icon_src', iconData.icon_src);
      diagram.model.commitTransaction('update icon');
      this.selectedNodeData.set({ ...data });
    }
    this.showIconPicker.set(false);
  }

  clearNodeIcon(): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (selected instanceof go.Node) {
      const data = selected.data as GojsEditorNodeData;
      diagram.model.startTransaction('clear icon');
      diagram.model.setDataProperty(data, 'icon', '');
      diagram.model.setDataProperty(data, 'icon_text', '');
      diagram.model.setDataProperty(data, 'icon_src', '');
      diagram.model.commitTransaction('clear icon');
      this.selectedNodeData.set({ ...data });
    }
    this.showIconPicker.set(false);
  }

  updateNodeProp(prop: string, value: unknown): void {
    this.editorService.setNodeDataProperty(prop, value);
  }

  updateLinkProp(prop: string, value: unknown): void {
    this.editorService.setLinkDataProperty(prop, value);
  }

  onTextInput(): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (!(selected instanceof go.Node)) {
      return;
    }
    const value = this.nodeTextCtrl.value;
    const data = selected.data as GojsEditorNodeData;

    if (data.is_math) {
      const parsed = parseSimpleLatex(value);
      diagram.model.startTransaction('update latex');
      diagram.model.setDataProperty(data, 'latex', value);
      diagram.model.setDataProperty(data, 'text', parsed.text);
      if (parsed.color) {
        diagram.model.setDataProperty(data, 'text_color', parsed.color);
      }
      diagram.model.commitTransaction('update latex');
    } else {
      this.updateNodeProp('text', value);
    }
  }

  sendToBack(): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (!(selected instanceof go.Node)) {
      return;
    }
    let minZ = 0;
    diagram.nodes.each((node) => {
      const z = (node.data as GojsEditorNodeData).z_order ?? 0;
      if (z < minZ) {
        minZ = z;
      }
    });
    const data = selected.data as GojsEditorNodeData;
    diagram.model.startTransaction('send to back');
    diagram.model.setDataProperty(data, 'z_order', minZ - 1);
    diagram.model.commitTransaction('send to back');
    this.selectedNodeData.set({ ...data });
  }

  bringToFront(): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (!(selected instanceof go.Node)) {
      return;
    }
    let maxZ = 0;
    diagram.nodes.each((node) => {
      const z = (node.data as GojsEditorNodeData).z_order ?? 0;
      if (z > maxZ) {
        maxZ = z;
      }
    });
    const data = selected.data as GojsEditorNodeData;
    diagram.model.startTransaction('bring to front');
    diagram.model.setDataProperty(data, 'z_order', maxZ + 1);
    diagram.model.commitTransaction('bring to front');
    this.selectedNodeData.set({ ...data });
  }

  onSizeChange(): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (!(selected instanceof go.Node)) {
      return;
    }
    const w = this.nodeWidthCtrl.value;
    const h = this.nodeHeightCtrl.value;
    if (w && h && w > 0 && h > 0) {
      const shape = selected.findObject('SHAPE') as go.Shape | null;
      diagram.model.startTransaction('set size');
      const data = selected.data as GojsEditorNodeData;
      diagram.model.setDataProperty(data, 'node_width', w);
      diagram.model.setDataProperty(data, 'node_height', h);
      if (shape) {
        shape.desiredSize = new go.Size(w, h);
      }
      diagram.model.commitTransaction('set size');
      this.selectedNodeData.set({ ...data });
    }
  }

  onMathToggle(checked: boolean): void {
    const diagram = this.editorService.getDiagram();
    if (!diagram) {
      return;
    }
    const selected = diagram.selection.first();
    if (!(selected instanceof go.Node)) {
      return;
    }
    const data = selected.data as GojsEditorNodeData;
    const model = diagram.model;

    model.startTransaction('toggle math');
    model.setDataProperty(data, 'is_math', checked);

    if (checked) {
      const currentText = data.text ?? '';
      model.setDataProperty(data, 'latex', currentText);
      model.setCategoryForNodeData(data, 'math');
    } else {
      model.setDataProperty(data, 'latex', '');
      model.setCategoryForNodeData(data, '');
    }

    model.commitTransaction('toggle math');

    const newNode = diagram.findNodeForData(data);
    if (newNode) {
      diagram.select(newNode);
    }

    const inputValue = checked ? data.latex ?? '' : data.text ?? '';
    this.nodeTextCtrl.setValue(inputValue, { emitEvent: false });
    this.selectedNodeData.set({ ...data });
  }

  onLinkColorChange(color: string): void {
    this.linkColorCtrl.setValue(color, { emitEvent: false });
    this.updateLinkProp('color', color);
  }

  applyLayout(layoutType: string): void {
    this.editorService.applyLayout(layoutType, this.autoLayout());
  }

  onAutoLayoutToggle(checked: boolean): void {
    this.autoLayout.set(checked);
    if (checked) {
      this.editorService.applyLayout(this.layoutCtrl.value, true);
    } else {
      this.editorService.freezeLayout();
    }
  }

  onShowBackgroundToggle(checked: boolean): void {
    this.showBackground.set(checked);
    this.editorService.applyNodeColors(checked);
  }

  ionToggleChecked(event: ToggleCustomEvent): boolean {
    return event.detail.checked;
  }

  save(): void {
    const extracted = this.editorService.extractModel();
    if (extracted) {
      this.pendingResult = {
        ...extracted,
        layoutType: this.layoutCtrl.value,
        height: this.heightCtrl.value,
      };
    }
    this.modal.dismiss();
  }

  cancel(): void {
    this.pendingResult = null;
    this.modal.dismiss();
  }

  onDidDismiss(): void {
    if (this.pendingResult) {
      this.modelChange.emit(this.pendingResult);
      this.pendingResult = null;
    }
    this.editorService.destroy();
    this.initialized = false;
    this.editorClose.emit();
  }

  private initEditor(): void {
    const editorEl = this.editorDiv?.nativeElement;
    const paletteEl = this.paletteDiv?.nativeElement;
    if (!editorEl || !paletteEl) {
      return;
    }

    this.editorService.initDiagram(editorEl, paletteEl, this.layoutCtrl.value, () =>
      this.syncFormControlsFromSelection(),
    );
    this.editorService.loadModel(this.model(), this.showBackground());
  }

  private syncFormControlsFromSelection(): void {
    this.showIconPicker.set(false);
    const nodeData = this.selectedNodeData();
    const linkData = this.selectedLinkData();

    if (nodeData) {
      const textValue = nodeData['is_math'] ? (nodeData['latex'] as string) ?? '' : (nodeData['text'] as string) ?? '';
      this.nodeTextCtrl.setValue(textValue, { emitEvent: false });
      this.nodeShapeCtrl.setValue((nodeData['shape'] as string) ?? 'RoundedRectangle', { emitEvent: false });
      this.nodeColorThemeCtrl.setValue((nodeData['color_theme'] as string) ?? 'neutral', { emitEvent: false });
      this.nodeFontSizeCtrl.setValue((nodeData['font_size'] as number) ?? 14, { emitEvent: false });
      this.nodeFontWeightCtrl.setValue((nodeData['font_weight'] as string) ?? 'normal', { emitEvent: false });
      this.nodeWidthCtrl.setValue((nodeData['node_width'] as number) ?? null, { emitEvent: false });
      this.nodeHeightCtrl.setValue((nodeData['node_height'] as number) ?? null, { emitEvent: false });
    } else if (linkData) {
      this.linkWidthCtrl.setValue((linkData['width'] as number) ?? 1.5, { emitEvent: false });
      this.linkColorCtrl.setValue((linkData['color'] as string) ?? '#8a8886', { emitEvent: false });
      this.linkArrowCtrl.setValue((linkData['to_arrow'] as string) ?? 'Triangle', { emitEvent: false });
    }
  }
}
